import React, { useEffect, useRef, useState } from "react";
import styles from "./style.module.scss";
import { Row, Col, Card, Table, Empty, DatePicker, Modal } from "antd";
import QuestionPopover from "../../components/questionPopover";
import moment, { Moment } from "moment";
import { QUERY } from "../../utilities/network";

const { MonthPicker } = DatePicker;

export default function AchievementsPage() {
	useEffect(() => {
		fetchGroupIds().then(() => fetchData(moment()));
	}, []);

	const groupIdsRef = useRef<Array<number>>([]);

	const [achievementData, setAchievementData] = useState<{
		most_engage_group: Array<GroupAchievementTableData>;
		most_dedicated_group: Array<GroupAchievementTableData>;
		most_ambitious_group: Array<GroupAchievementTableData>;
		the_sprint_group: Array<GroupAchievementTableData>;
	}>({
		most_engage_group: [],
		most_dedicated_group: [],
		most_ambitious_group: [],
		the_sprint_group: [],
	});

	function onMonthPickerChange(moment: Moment | null, dateString: string) {
		if (moment) {
			fetchData(moment).then();
		}
	}

	async function fetchGroupIds() {
		try {
			const data = await QUERY<{ groups: Array<{ id: number }> }>(
				FETCH_GROUP_IDS
			);
			groupIdsRef.current = data.groups.map((e) => e.id);
		} catch (e) {
			Modal.error({
				title: "Oops, something goes wrong.",
				content: e.message,
				okText: "GOT IT",
			});
		}
	}

	async function fetchData(moment: Moment): Promise<void> {
		const year = moment.year();
		const month = moment.month() + 1;

		try {
			const res = await QUERY<{ achievement: GroupAchievement }>(
				FETCH_GROUP_ACHIEVEMENTS,
				{
					groupIds: groupIdsRef.current,
					year,
					month,
				}
			);

			const {
				most_engage_group,
				most_ambitious_group,
				most_dedicated_group,
				the_sprint_group,
			} = res.achievement;

			// { rank: number; name: string; value: string }
			const achievementData = {
				most_engage_group: most_engage_group.map((e, i) => ({
					rank: i + 1,
					name: e.group_name,
					value: `${e.active_leaders_percentage}%`,
				})),
				most_dedicated_group: most_dedicated_group.map((e, i) => ({
					rank: i + 1,
					name: e.programme_name,
					value: (
						e.total_goals / Math.max(e.total_members, 1)
					).toFixed(1),
				})),
				most_ambitious_group: most_ambitious_group.map((e, i) => ({
					rank: i + 1,
					name: e.programme_name,
					value: e.avg_activities.toFixed(1),
				})),
				the_sprint_group: the_sprint_group.map((e, i) => ({
					rank: i + 1,
					name: e.programme_name,
					value: `${e.change_from_last_month}%`,
				})),
			};
			setAchievementData(achievementData);
		} catch (e) {
			Modal.error({
				title: "Oops, something goes wrong.",
				content: e.message,
				okText: "GOT IT",
			});
		}
	}

	return (
		<>
			<article>Group Achievements</article>
			<article className='subHeadding'>
				<h6>
					Showing monthly data to track and recognize training efforts
					on group level.
				</h6>
			</article>
			<div className={styles.monthPickerRow}>
				<MonthPicker
					size='large'
					allowClear={false}
					placeholder='Choose month…'
					defaultValue={moment()}
					onChange={onMonthPickerChange}
				/>
			</div>

			<Row gutter={16}>
				<GroupAchievementTable
					title='Most Engaged Group'
					description='Showing a percentage of how many leaders who have been active in the group, for the chosen month.'
					columnName='Active leaders'
					data={achievementData.most_engage_group}
				/>
				<GroupAchievementTable
					title='Most Dedicated Group'
					description='Showing how many weekly activity goals that have been reached divided by the total number of activated leaders in the group, for the chosen month.'
					columnName='Avg weekly goal completion'
					data={achievementData.most_dedicated_group}
				/>
			</Row>
			<div style={{ height: 16 }} />
			<Row gutter={16}>
				<GroupAchievementTable
					title='Most Ambitious Group'
					description='Showing completed training activities divided by total number of activated members in the group, for the chosen month.'
					columnName='Avg completed activities'
					data={achievementData.most_ambitious_group}
				/>
				<GroupAchievementTable
					title='The Sprint Group'
					description='Showing the biggest improvement of average training activities completed compared to last month.'
					columnName='Change from last month'
					data={achievementData.the_sprint_group}
				/>
			</Row>
		</>
	);
}

interface GroupAchievementTableData {
	rank: number;
	name: string;
	value: string;
}

interface GroupAchievementTableProps {
	title: string;
	description: string;
	columnName: string;
	data: Array<GroupAchievementTableData>;
}

function GroupAchievementTable({
	title,
	description,
	columnName,
	data,
}: GroupAchievementTableProps) {
	const columns: any = [
		{
			title: "Rank",
			dataIndex: "rank",
		},
		{
			title: "Group name",
			dataIndex: "name",
		},
		{
			title: columnName,
			dataIndex: "value",
		},
	];
	const emptyText = (
		<Empty
			style={{ paddingTop: 10, height: 165 }}
			description={<span>Not enough data yet to display results</span>}
		/>
	);

	return (
		<Col span={12}>
			<Card
				title={title}
				extra={<QuestionPopover content={description} />}
			>
				<div>
					<Table
						columns={columns}
						dataSource={data}
						size='small'
						locale={{ emptyText }}
						pagination={false}
						rowKey={(record) => `${record.rank}${record.name}`}
					/>
				</div>
			</Card>
		</Col>
	);
}

const FETCH_GROUP_IDS = `
query {
  groups {
    id: group_id
  }
}`;
const FETCH_GROUP_ACHIEVEMENTS = `
query($groupIds: [Int!]!, $year: Int!, $month: Int!) {
  achievement(year: $year, month: $month, groupIds: $groupIds)
}`;

interface GroupAchievement {
	most_engage_group: Array<{
		group_id: number;
		group_name: string;
		active_leaders_percentage: number;
	}>;
	most_dedicated_group: Array<{
		programme_id: number;
		programme_name: string;
		total_members: number;
		total_goals: number;
	}>;
	most_ambitious_group: Array<{
		programme_id: number;
		programme_name: string;
		avg_activities: number;
	}>;
	the_sprint_group: Array<{
		programme_id: number;
		programme_name: string;
		change_from_last_month: number;
	}>;
}
